import React, { useState } from 'react';
import AppSidebar, { AppSidebarProps, defaultNavGroups } from './AppSidebar';
import AppTopBar, { AppTopBarProps } from './AppTopBar';
import SidebarRail from './SidebarRail';
import { Sheet } from '../ui';
import { SHELL_SIDEBAR_RAIL, useSidebarRail } from '../../hooks/useSidebarRail';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * v2 app shell (TASK-105) — the persistent chrome every non-wizard page lives
 * in: navy nav + white top bar + #F4F6FA canvas. The wizard has its own chrome
 * (TASK-106). Presentational: pages pass nav state, top-bar config and
 * content; floating children (Robbie panel, TASK-107) can absolutely position
 * against the relative root.
 *
 * The nav has two shapes, and which one is showing is the user's choice
 * (remembered per browser, default open):
 *   - pinned (default): the full sidebar sits in flow at `w-nav-rail` and the
 *     layout makes room for it, exactly as the shell shipped. Somebody
 *     arriving for the first time should be able to read where the product's
 *     pages are, not decode five unlabelled icons.
 *   - rail: the slim icon column keeps its place, and hovering or focusing it
 *     floats the full sidebar OVER the canvas. The main column does not move —
 *     a panel that appears under the pointer must not shove the page the
 *     pointer was heading for.
 *
 * On a phone it has a third, and the choice stops being the user's: neither
 * shape fits. The full rail is 190px of a 375px screen and the slim one is a
 * column of icons revealed by a hover that a finger cannot perform. So the nav
 * comes out of the layout entirely and becomes a drawer behind the top bar's
 * menu button — the whole width goes to the page, which is the only thing
 * anyone opened the app to read.
 */
export interface AppShellProps {
  sidebar?: Partial<AppSidebarProps>;
  topBar: AppTopBarProps;
  /** Height of the shell; pages keep the default full viewport. */
  className?: string;
  /** Canvas padding per the mock (20px 24px); override for full-bleed pages. */
  contentClassName?: string;
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  sidebar,
  topBar,
  className = 'h-app',
  contentClassName = 'px-s5 py-s5 md:px-s7 md:py-[20px]',
  children,
}) => {
  const rail = useSidebarRail(SHELL_SIDEBAR_RAIL);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = { groups: defaultNavGroups, ...sidebar };

  if (isMobile) {
    return (
      <div className={`relative flex flex-col overflow-hidden bg-canvas font-sans ${className}`}>
        <AppTopBar {...topBar} onMenu={() => setDrawerOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <div className={`w-full ${contentClassName}`}>{children}</div>
        </main>
        <Sheet open={drawerOpen} onClose={() => setDrawerOpen(false)} side="left" label="Navigation">
          <AppSidebar
            {...nav}
            // No pin control: there is nothing to pin it to. The drawer is
            // the only shape the nav has here.
            onNavigate={(id) => {
              setDrawerOpen(false);
              nav.onNavigate?.(id);
            }}
            className="h-full w-full overflow-y-auto"
          />
        </Sheet>
      </div>
    );
  }

  return (
    <div className={`relative flex overflow-hidden bg-canvas font-sans ${className}`}>
      <div
        ref={rail.containerRef}
        className={`relative z-40 h-full shrink-0 ${rail.pinned ? 'w-nav-rail' : 'w-nav-rail-slim'}`}
        onPointerEnter={rail.handleEnter}
        onPointerLeave={rail.handleLeave}
        onFocus={rail.handleFocus}
        onBlur={rail.handleBlur}
      >
        {rail.pinned ? (
          <AppSidebar
            {...nav}
            pinned
            onTogglePin={rail.togglePinned}
            className={`w-nav-rail ${nav.className ?? ''}`}
          />
        ) : (
          <>
            <SidebarRail
              groups={nav.groups}
              activeId={nav.activeId}
              onNavigate={nav.onNavigate}
              user={nav.user}
              pinned={false}
              onTogglePin={rail.togglePinned}
              pinButtonRef={rail.railButtonRef}
              className="w-nav-rail-slim"
            />
            {/* Overlay, not a column: absolute inside the rail's own slot, so
                the canvas keeps every pixel it had. Visibility rides the same
                transition as opacity so the panel fades out instead of being
                yanked, while staying out of the tab order when closed. */}
            <div
              className={`absolute inset-y-0 left-0 transition-[opacity,transform,visibility] duration-150 ease-out motion-reduce:transition-none ${
                rail.open
                  ? 'visible translate-x-0 opacity-100'
                  : 'invisible -translate-x-s3 opacity-0'
              }`}
              aria-hidden={rail.open ? undefined : true}
            >
              <AppSidebar
                {...nav}
                pinned={false}
                onTogglePin={rail.togglePinned}
                className={`w-nav-rail shadow-floating ${nav.className ?? ''}`}
              />
            </div>
          </>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AppTopBar {...topBar} />
        <main className="flex-1 overflow-y-auto">
          {/* Full-width content column — side padding only, no outer clamp
              (owner refinement, 29 Jul). This shell is shared by every
              non-wizard page, so width discipline belongs to each page's own
              inner content (e.g. the dashboard's KPI grid / pipeline band /
              table already reflow sensibly on their own), not to this
              generic column. */}
          <div className={`w-full ${contentClassName}`}>{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AppShell;
